import axios from 'axios';
import React, { useContext, useState } from 'react'
import dayjs from '../dayjs';

const AppContext = React.createContext();

const API_URL = process.env.REACT_APP_API_URL || "https://run.salesblink.io/api/meet/slug"
const SLUG = window.location.pathname.split("/").filter(Boolean).pop() || window.location.hostname;

function buildUrl(slug, timezone) {
    const url = new URL(`${API_URL}/${slug}`);
    if (timezone) {
        url.searchParams.set("timezone", timezone);
    }
    return url.toString();
}

function generateMarkedDates(config, timezone) {
    const workingHours = config?.meeting_working_hours || [];
    const dateRange = config?.date_range ?? 30;
    const holidays = config?.holidays || [];
    // honor the minimum notice period (in days) when generating fallback dates
    const bookingBuffer = config?.last_minute_booking_buffer;
    const bufferDays = bookingBuffer?.label === 'days' ? parseInt(bookingBuffer?.value) || 0 : 0;

    const enabledDays = new Set(
        workingHours.filter((d) => d.enabled).map((d) => d.day)
    );
    const holidaySet = new Set(holidays);
    const dates = [];
    const tz = timezone || dayjs.tz.guess() || "UTC";
    const start = dayjs().tz(tz).startOf('day').add(bufferDays, 'day');

    for (let i = 0; i <= dateRange; i++) {
        const d = start.add(i, 'day');
        const dayName = d.format('dddd');
        if (!enabledDays.has(dayName)) continue;
        if (holidaySet.has(d.format('YYYY-MM-DD'))) continue;
        dates.push(d.format('DD-MM-YYYY'));
    }

    return dates;
}

export function useAppData() {
    return useContext(AppContext);
}

export function AppProvider({ children }) {
    const [apiData, setApiData] = useState(null)
    const [error, setError] = useState(null)
    const [errorDescription, setErrorDescription] = useState(null)
    const [dates, setDates] = useState([])

    async function fetchData(timezone) {
        try {
            const url = buildUrl(SLUG, timezone);
            let { data } = await axios.get(url)
            setApiData({...data.data, whitelabelCalendar: data.whitelabelCalendar});
            // Ignore invalid date strings returned by the backend
            const validDates = (data.dates || []).filter(d => /^\d{2}-\d{2}-\d{4}$/.test(d));
            // Only generate dates from the calendar config when the backend sent none at all.
            // An empty list means nothing is bookable (e.g. fully booked), so show no dates.
            setDates(Array.isArray(data.dates) ? validDates : generateMarkedDates(data.data, timezone))
        } catch(err) {
            if(err.response?.status === 429) setError(err.response?.data)
            else if(err.response?.data?.title) setError(err.response?.data?.title)
            else if(err.request) setError("Network error. Please try again.")
            else setError("Something went wrong.")
        }
    }

    return (
        <AppContext.Provider value={{ apiData, fetchData, dates, error, setError, errorDescription, setErrorDescription }}>
            {children}
        </AppContext.Provider>
    )
}
